import { bewegungsPreis, jahrgangFuer } from "../kern/fachlogik.js";
import { wertFormat } from "../kern/format.js";
import { istOffeneKritik, kritikBezeichnung } from "../kern/kritik.js";

/* ================================================================
   Trinkverlauf — was ist wann in den Keller gekommen und was ist
   herausgegangen.

   Grundlage ist das Bewegungslog (db.bewegungen). Es kennt drei
   Arten:

     zugang     eingelagert
     entnahme   entnommen (getrunken, verschenkt, verkocht …)
     weinkarte  auf eine Weinkarte gesetzt — die Flasche verlässt
                den Bestand, ist aber noch nicht getrunken

   Dazu kommen als vierte Art die Kritiken. Sie bewegen keine
   Flasche, gehören aber in dieselbe Chronik: Wann eine Flasche
   getrunken wurde und was man von ihr hielt, sind zwei Hälften
   desselben Vorgangs. In die Flaschensummen gehen sie deshalb
   NICHT ein — dort zählen nur Bewegungen.

   Kritiken tragen ihren Zeitstempel im Feld `erfasst`. Ältere
   Kritiken haben keinen — dann tritt die Entnahme desselben
   Jahrgangs an seine Stelle: Eine Verkostungsnotiz entsteht in aller
   Regel an der Flasche, die man gerade getrunken hat, und die App
   fragt nach einer Entnahme sogar aktiv danach. Der Eintrag steht
   damit dort, wo er inhaltlich hingehört, statt am Ende der Liste
   zu versacken.

   Gibt es mehrere Entnahmen dieses Jahrgangs, gewinnt die jüngste:
   Sie ist die wahrscheinlichste Gelegenheit für eine noch nicht
   datierte Notiz. Sicher ist das nicht, deshalb wird ein so
   geliehenes Datum als `datumGeliehen` gekennzeichnet und in der
   Anzeige entsprechend ausgewiesen — als Tatsache ausgeben würde
   heißen, eine Angabe zu erfinden.

   Erst wenn dieser Jahrgang nie entnommen wurde, bleibt es bei
   „Ohne Datum". Auf einen anderen Jahrgang auszuweichen wäre
   geraten, nicht geschlossen.

   db.entnahmen wird bewusst NICHT zusätzlich ausgewertet: Jede
   Entnahme steht ohnehin auch im Bewegungslog, und zwar mit
   derselben ID. Zwei Quellen zu mischen hieße, Einträge doppelt zu
   zeigen, sobald beide vorhanden sind.

   Hier wird nur gelesen und aufbereitet — gebucht wird anderswo.
   ================================================================ */

const ARTEN = ["zugang", "entnahme", "weinkarte", "kritik"];

/* Bewegt Flaschen? Kritiken tun das nicht. */
const istBewegung = (art) => art !== "kritik";

/* Vorzeichen einer Bewegung für den Bestand. */
const istZugang = (art) => art === "zugang";

/* Monatsschlüssel „2026-08" aus einem ISO-Zeitstempel. Ohne Datum
   leer — solche Einträge landen gesammelt am Ende. */
function monatsSchluessel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function jahrVon(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.getFullYear();
}

/* Eine Bewegung um alles anreichern, was die Anzeige braucht:
   Wein- und Winzername, Jahrgang, Flaschenpreis und Gesamtwert. */
function reichereAn(b, { weinById, winzerById, karteById }) {
  const wein = weinById.get(b.weinId) || null;
  const winzer = wein ? winzerById.get(wein.winzerId) || null : null;
  const jg = wein ? jahrgangFuer(wein, b.jgId, b.jahr) : null;
  const preis = bewegungsPreis(jg, b);
  const menge = b.menge || 0;
  const karte = b.karteId ? karteById.get(b.karteId) || null : null;
  return {
    id: b.id,
    art: b.art,
    datum: b.datum || "",
    menge,
    groesse: b.groesse,
    jahr: b.jahr != null ? b.jahr : (jg ? jg.jahr : null),
    weinId: b.weinId,
    jgId: b.jgId,
    offen: false,
    weinName: wein ? wein.name : "",
    winzerName: winzer ? winzer.name : "",
    haendler: b.haendler || "",
    karteName: karte ? karte.name || "" : "",
    preis,
    wert: preis != null ? preis * menge : null,
    /* Für die Bearbeitung wird die ursprüngliche Bewegung gebraucht. */
    bewegung: b,
  };
}

/* Schlüssel, unter denen ein Jahrgang wiedergefunden wird. Zwei
   davon, weil ältere Bewegungen nur das Jahr kennen und neuere die
   jgId — beide Wege müssen treffen. */
function jahrgangsSchluessel(weinId, jgId, jahr) {
  const s = [];
  if (weinId && jgId) s.push(`${weinId}|${jgId}`);
  if (weinId && jahr != null) s.push(`${weinId}@${jahr}`);
  return s;
}


/* Jüngstes Entnahmedatum je Jahrgang — Ersatzdatum für Kritiken ohne
   eigenen Zeitstempel. */
function letzteEntnahmen(db) {
  const karte = new Map();
  for (const b of db.bewegungen || []) {
    if (b.art !== "entnahme" || !b.datum) continue;
    for (const key of jahrgangsSchluessel(b.weinId, b.jgId, b.jahr)) {
      const bisher = karte.get(key);
      if (!bisher || b.datum > bisher) karte.set(key, b.datum);
    }
  }
  return karte;
}


/* Eine Kritik in dieselbe Form bringen wie eine Bewegung, damit
   Sortierung, Gruppierung und Suche nicht zwei Fälle kennen müssen. */
function kritikAnreichern(k, { weinById, winzerById, entnahmeDatum }) {
  const offen = istOffeneKritik(k);
  const wein = offen ? null : (weinById.get(k.weinId) || null);
  const winzer = wein ? winzerById.get(wein.winzerId) || null : null;
  const jg = wein ? jahrgangFuer(wein, k.jgId, k.jahr) : null;
  const jahr = offen ? null : (k.jahr != null ? k.jahr : (jg ? jg.jahr : null));

  /* Ohne eigenen Zeitstempel: Datum der jüngsten Entnahme dieses
     Jahrgangs leihen. Bei einer offenen Kritik gibt es keinen
     Jahrgang, von dem sich etwas leihen ließe — sie trägt ihren
     Zeitstempel aber ohnehin immer, weil sie neu entsteht. */
  let datum = k.erfasst || "";
  let geliehen = false;
  if (!datum && !offen && entnahmeDatum) {
    for (const key of jahrgangsSchluessel(k.weinId, k.jgId, jahr)) {
      const treffer = entnahmeDatum.get(key);
      if (treffer) { datum = treffer; geliehen = true; break; }
    }
  }

  return {
    id: k.id,
    art: "kritik",
    datum,
    datumGeliehen: geliehen,
    menge: 0,
    groesse: null,
    jahr,
    weinId: k.weinId,
    jgId: k.jgId,
    offen,
    weinName: wein ? wein.name : kritikBezeichnung(k),
    winzerName: winzer ? winzer.name : "",
    haendler: "",
    karteName: "",
    kritiker: k.kritiker || "",
    bewertung: wertFormat(k.schema, k.wert),
    preis: null,
    wert: null,
    kritik: k,
  };
}


/* Volltextsuche über Wein, Winzer, Händler, Weinkarte und Kritiker. */
function passtZurSuche(e, suchtext) {
  if (!suchtext) return true;
  const s = suchtext.trim().toLowerCase();
  if (!s) return true;
  return [e.weinName, e.winzerName, e.haendler, e.karteName, e.kritiker || "",
          e.jahr != null ? String(e.jahr) : ""]
    .some((f) => String(f).toLowerCase().includes(s));
}

/* Summiert Flaschen und Wert je Richtung. „aus" fasst Entnahmen und
   Weinkarten zusammen — beides verlässt den Keller. */
function summiere(eintraege) {
  const s = { ein: 0, aus: 0, einWert: 0, ausWert: 0, entnommen: 0, weinkarte: 0, kritiken: 0, offen: 0 };
  for (const e of eintraege) {
    if (!istBewegung(e.art)) { s.kritiken += 1; if (e.offen) s.offen += 1; continue; }
    if (istZugang(e.art)) {
      s.ein += e.menge;
      s.einWert += e.wert || 0;
    } else {
      s.aus += e.menge;
      s.ausWert += e.wert || 0;
      if (e.art === "weinkarte") s.weinkarte += e.menge;
      else s.entnommen += e.menge;
    }
  }
  s.saldo = s.ein - s.aus;
  return s;
}

/* Gruppiert die bereits sortierte Liste in Monatsabschnitte. Die
   Reihenfolge der Gruppen folgt der Liste, damit Sortierung und
   Gruppierung nicht auseinanderlaufen können. */
function nachMonaten(eintraege) {
  const gruppen = [];
  const index = new Map();
  for (const e of eintraege) {
    const key = monatsSchluessel(e.datum);
    let g = index.get(key);
    if (!g) {
      g = { schluessel: key, datum: e.datum || "", eintraege: [] };
      index.set(key, g);
      gruppen.push(g);
    }
    g.eintraege.push(e);
  }
  for (const g of gruppen) g.summe = summiere(g.eintraege);
  return gruppen;
}

/* Alle Jahre, in denen etwas gebucht wurde — für die Jahresauswahl.
   Absteigend, damit das jüngste Jahr oben steht. */
function jahreImVerlauf(db) {
  const menge = new Set();
  for (const b of db.bewegungen || []) {
    const j = jahrVon(b.datum);
    if (j != null) menge.add(j);
  }
  for (const k of db.kritiken || []) {
    const j = jahrVon(k.erfasst);
    if (j != null) menge.add(j);
  }
  return [...menge].sort((a, b) => b - a);
}

/* Hauptzugang: aufbereiteter, gefilterter und gruppierter Verlauf.

   art    "alle" | "zugang" | "entnahme" | "weinkarte" | "kritik"
          | "offen" — Sonderfall: keine Bewegungsart, sondern die
          noch nicht zugeordneten Kritiken. Steht in derselben
          Filterreihe, weil der Nutzer dort nach ihnen sucht.
   jahr   Kalenderjahr oder null für alle
   suche  Freitext über Wein, Winzer, Händler, Weinkarte, Jahrgang

   Die Summen beziehen sich immer auf das gefilterte Ergebnis — was
   man sieht, ist auch das, was gezählt wird. */
function baueVerlauf(db, { art = "alle", jahr = null, suche = "" } = {}) {
  const weinById = new Map((db.weine || []).map((w) => [w.id, w]));
  const winzerById = new Map((db.winzer || []).map((w) => [w.id, w]));
  const karteById = new Map((db.weinkarten || []).map((k) => [k.id, k]));
  const nachschlag = { weinById, winzerById, karteById, entnahmeDatum: letzteEntnahmen(db) };

  const alle = [
    ...(db.bewegungen || []).map((b) => reichereAn(b, nachschlag)),
    ...(db.kritiken || []).map((k) => kritikAnreichern(k, nachschlag)),
  ];

  const gefiltert = alle.filter((e) => {
    if (art === "offen") { if (!e.offen) return false; }
    else if (art !== "alle" && e.art !== art) return false;
    if (jahr != null && jahrVon(e.datum) !== jahr) return false;
    return passtZurSuche(e, suche);
  });

  /* Neueste zuerst. Bei gleichem Zeitstempel entscheidet die ID,
     damit die Reihenfolge zwischen zwei Aufrufen stabil bleibt. */
  gefiltert.sort((a, b) => (b.datum || "").localeCompare(a.datum || "")
    || String(a.id).localeCompare(String(b.id)));

  return {
    eintraege: gefiltert,
    monate: nachMonaten(gefiltert),
    jahre: jahreImVerlauf(db),
    summe: summiere(gefiltert),
    gesamt: alle.length,
  };
}

export { ARTEN, baueVerlauf, istBewegung, jahreImVerlauf, letzteEntnahmen, monatsSchluessel, summiere };
